<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">{{ editing ? 'Editar movimiento' : 'Nuevo movimiento' }}</h1>
    </div>

    <form class="custom-form" @submit.prevent="save" novalidate>
      <wz-card :title="editing ? 'Datos del movimiento' : 'Datos del nuevo movimiento'">
        <section class="form-section mb-0">
          <div class="flex flex-wrap -mx-2">

            <!-- Empleado -->
            <div class="w-full px-2" v-if="!editing">
              <div class="mb-3">
                <wz-select-search
                  label="Colaborador"
                  required
                  :items="employeeOptions"
                  v-model="form.employeeId"
                  defaultOption="Buscar por nombre o número de documento"
                  :error="$v.form.employeeId.$error && !$v.form.employeeId.required ? 'El colaborador es requerido' : ''"
                  @change="onEmployeeSelected"
                />
              </div>
            </div>

            <!-- Alerta: sin contrato vigente -->
            <div class="w-full px-2" v-if="noActiveContract">
              <wz-alert variant="danger" icon="ri-error-warning-line" class="mb-3">
                Este colaborador no tiene contrato vigente.
              </wz-alert>
            </div>

            <div class="w-full px-2" v-if="complianceError">
              <wz-alert variant="danger" icon="ri-lock-line" class="mb-3">
                <strong>Documentación incompleta.</strong> No se puede registrar el movimiento.
                Documentos faltantes: {{ complianceError.join(', ') }}
              </wz-alert>
            </div>

            <!-- Concepto -->
            <div class="w-full px-2">
              <div class="mb-3">
                <wz-select
                  label="Concepto"
                  required
                  :items="conceptOptions"
                  v-model="form.conceptId"
                  defaultOption="Elegir concepto..."
                  placeholder="Buscar concepto"
                  @change="onConceptSelected"
                  :error="$v.form.conceptId.$error && !$v.form.conceptId.required ? 'El concepto es requerido' : ''"
                />
                <!-- Concept badge preview -->
                <div v-if="selectedConcept" class="mt-2">
                  <wz-badge class="mr-2" :variant="selectedConcept.type === 'DESCUENTO' ? 'danger-solid' : 'success-solid'">
                    {{ selectedConcept.type === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                  </wz-badge>
                  <span class="text-muted text-sm">{{ getNatureLabel(selectedConcept.nature) }}</span>
                </div>
              </div>
            </div>

            <!-- Período: Mes -->
            <div class="w-full md:w-1/3 px-2">
              <div class="mb-3">
                <wz-select
                  label="Mes"
                  required
                  :items="monthOptions"
                  v-model="form.periodMonth"
                  defaultOption="Seleccione mes"
                  :error="$v.form.periodMonth.$error && !$v.form.periodMonth.required ? 'El mes es requerido' : ''"
                />
              </div>
            </div>

            <!-- Período: Año -->
            <div class="w-full md:w-1/3 px-2">
              <div class="mb-3">
                <wz-select
                  label="Año"
                  required
                  :items="yearOptions"
                  v-model="form.periodYear"
                  defaultOption="Seleccione año"
                  :error="$v.form.periodYear.$error && !$v.form.periodYear.required ? 'El año es requerido' : ''"
                />
              </div>
            </div>

            <!-- Moneda -->
            <div class="w-full md:w-1/3 px-2">
              <div class="mb-3">
                <wz-select
                  label="Moneda"
                  :items="currencyOptions"
                  v-model="form.currency"
                />
              </div>
            </div>

            <!-- Monto total -->
            <div class="w-full md:w-1/2 px-2">
              <div class="mb-3">
                <wz-input
                  label="Monto total"
                  required
                  type="number"
                  v-model="form.amount"
                  placeholder="0.00"
                  :error="$v.form.amount.$error
                    ? (!$v.form.amount.required ? 'El monto es requerido'
                      : !$v.form.amount.positiveAmount ? 'El monto debe ser mayor a 0' : '')
                    : ''"
                />
              </div>
            </div>

            <!-- N° cuotas -->
            <div class="w-full md:w-1/2 px-2" v-if="!editing">
              <div class="mb-3">
                <wz-input
                  label="N° cuotas"
                  type="number"
                  v-model="form.totalInstallments"
                  placeholder="1"
                />
                <small class="text-muted">Dejar en 1 para movimiento puntual</small>
              </div>
            </div>

            <!-- Cuota estimada + info (solo si cuotas > 1) -->
            <div class="w-full px-2" v-if="hasMultipleInstallments && !editing">
              <div class="flex flex-wrap -mx-2">
                <div class="w-full md:w-1/3 px-2">
                  <div class="mb-3">
                    <wz-input label="Cuota estimada" :value="estimatedInstallment" disabled />
                  </div>
                </div>
              </div>
              <wz-alert variant="info" icon="ri-information-line" class="mb-3">
                Se generarán <strong>{{ form.totalInstallments }}</strong> descuentos de
                <strong>{{ estimatedInstallment }}</strong>
                desde <strong>{{ startPeriodLabel }}</strong> hasta <strong>{{ endPeriodLabel }}</strong>.
              </wz-alert>
            </div>

            <!-- Descripción -->
            <div class="w-full px-2">
              <div class="mb-3">
                <wz-textarea
                  label="Descripción"
                  v-model="form.description"
                  :rows="3"
                  placeholder="Descripción opcional"
                />
              </div>
            </div>

            <!-- Sección comprobante (solo creación) -->
            <template v-if="!editing">
              <div class="w-full px-2">
                <hr />
                <h6 class="text-muted mb-3">Comprobante (opcional)</h6>
              </div>

              <!-- Tipo comprobante -->
              <div class="w-full md:w-1/3 px-2">
                <div class="mb-3">
                  <wz-select
                    label="Tipo de comprobante"
                    :items="voucherTypeOptions"
                    v-model="form.voucherType"
                    defaultOption="Sin comprobante"
                  />
                </div>
              </div>

              <!-- N° comprobante -->
              <div class="w-full md:w-1/3 px-2" v-if="form.voucherType">
                <div class="mb-3">
                  <wz-input
                    label="N° comprobante"
                    v-model="form.voucherNumber"
                    placeholder="Ej: B001-05678"
                  />
                </div>
              </div>

              <!-- Archivo adjunto -->
              <div class="w-full px-2">
                <div class="mb-3">
                  <label class="wz-form-label">Documento adjunto</label>
                  <wz-file-input
                    id-file="movement-voucher-file"
                    :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
                    @onChange="onFileChange"
                  />
                  <small class="text-muted">Máx. 5MB</small>
                </div>
              </div>
            </template>

          </div>
        </section>

        <div class="mb-3 text-right">
          <wz-button variant="light" @click="$router.push({ name: 'payroll-movement-list' })">
            {{ editing ? 'Volver' : 'Cancelar' }}
          </wz-button>
          <wz-button type="submit" variant="primary" class="ml-2" :disabled="(noActiveContract || complianceError) && !editing">Guardar</wz-button>
        </div>
      </wz-card>
    </form>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required } from 'vuelidate/lib/validators'
import { PayrollMovementService, PayrollConceptService, EmployeeService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

function positiveAmount(value) {
  return value != null && Number(value) > 0
}

export default {
  name: 'PayrollMovementCreate',
  

  data() {
    const now = new Date()
    return {
      editing:        false,
      noActiveContract: false,
      complianceError: null,
      employeeOptions: [],
      conceptOptions:  [],
      allConcepts:     [],
      selectedConcept: null,
      selectedFile:    null,
      monthOptions: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })),
      currencyOptions: [
        { key: 'PEN', label: 'PEN - Soles' },
        { key: 'USD', label: 'USD - Dólares' },
      ],
      voucherTypeOptions: Object.values(LocalConstants.VoucherType).map(v => ({ key: v.key, label: v.label })),
      form: {
        employeeId:        null,
        conceptId:         null,
        periodMonth:       now.getMonth() + 1,
        periodYear:        now.getFullYear(),
        currency:          'PEN',
        amount:            null,
        description:       null,
        totalInstallments: 1,
        voucherType:       null,
        voucherNumber:     null,
      },
    }
  },

  validations: {
    form: {
      employeeId:  { required },
      conceptId:   { required },
      periodMonth: { required },
      periodYear:  { required },
      amount:      { required, positiveAmount },
    },
  },

  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
    hasMultipleInstallments() {
      return Number(this.form.totalInstallments) > 1
    },
    estimatedInstallment() {
      const total = Number(this.form.amount)
      const n = Number(this.form.totalInstallments)
      if (!total || !n || n < 1) return '-'
      const prefix = this.form.currency === 'USD' ? 'US$ ' : 'S/ '
      return prefix + (Math.floor((total / n) * 100) / 100).toFixed(2)
    },
    startPeriodLabel() {
      const m = MONTH_NAMES[(this.form.periodMonth || 1) - 1]
      return `${m} ${this.form.periodYear}`
    },
    endPeriodLabel() {
      const n = Number(this.form.totalInstallments) || 1
      let month = (this.form.periodMonth || 1) + n - 1
      let year = this.form.periodYear || new Date().getFullYear()
      while (month > 12) { month -= 12; year++ }
      return `${MONTH_NAMES[month - 1]} ${year}`
    },
  },

  methods: {
    async onEmployeeSelected(id) {
      this.form.employeeId  = id
      this.noActiveContract = false
      this.complianceError  = null
      if (!id) return
      try {
        const compResp = await EmployeeService.getCompliance(id)
        const p = compResp.payload
        this.complianceError = p.compliant ? null : p.missingDocuments
      } catch (e) { /* non-critical */ }
    },

    onConceptSelected(id) {
      this.form.conceptId   = id
      this.selectedConcept  = this.allConcepts.find(c => c.id === id) || null
    },

    onFileChange(files) {
      const file = Array.isArray(files) ? files[0] : files
      if (file && file.size > 5 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 5 MB')
        this.selectedFile = null
        return
      }
      this.selectedFile = file || null
    },

    getNatureLabel(nature) {
      const n = LocalConstants.PayrollConceptNature[nature]
      return n ? n.label : nature
    },

    async save() {
      this.$v.$touch()
      if (this.$v.$error) return

      try {
        await this.$store.dispatch('app/loading', true)

        if (this.editing) {
          const data = {
            employeeId:  this.form.employeeId,
            conceptId:   this.form.conceptId,
            periodYear:  this.form.periodYear,
            periodMonth: this.form.periodMonth,
            amount:      Number(this.form.amount),
            currency:    this.form.currency,
            description: this.form.description || null,
          }
          await PayrollMovementService.update(this.$route.params.id, data)
        } else {
          const data = {
            employeeId:        this.form.employeeId,
            conceptId:         this.form.conceptId,
            periodYear:        this.form.periodYear,
            periodMonth:       this.form.periodMonth,
            amount:            Number(this.form.amount),
            currency:          this.form.currency,
            description:       this.form.description || undefined,
            totalInstallments: Number(this.form.totalInstallments) || 1,
            voucherType:       this.form.voucherType || undefined,
            voucherNumber:     this.form.voucherNumber || undefined,
          }
          await PayrollMovementService.create(data, this.selectedFile)
        }

        Alert.success('Se almacenó la información correctamente')
        this.$router.push({ name: 'payroll-movement-list' })
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async load(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = (await PayrollMovementService.get(id)).payload
        this.form = {
          employeeId:        payload.employeeId,
          conceptId:         payload.conceptId,
          periodMonth:       payload.periodMonth,
          periodYear:        payload.periodYear,
          currency:          payload.currency || 'PEN',
          amount:            payload.amount,
          description:       payload.description,
          totalInstallments: 1,
          voucherType:       null,
          voucherNumber:     null,
        }
        this.selectedConcept = this.allConcepts.find(c => c.id === payload.conceptId) || null
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el movimiento')
        this.$router.push({ name: 'payroll-movement-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  async created() {
    if (this.$route.query.employeeId) {
      this.form.employeeId = Number(this.$route.query.employeeId)
    }

    try {
      const [conceptsResp, employeesResp] = await Promise.all([
        PayrollConceptService.listActive(),
        EmployeeService.listAll(),
      ])
      this.allConcepts    = conceptsResp.payload || []
      this.conceptOptions = this.allConcepts.map(c => ({ key: c.id, label: `${c.code} - ${c.name}` }))
      this.employeeOptions = (employeesResp.payload || []).map(e => ({ key: e.id, label: e.description }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }

    if (this.$route.params.id) {
      this.load(this.$route.params.id)
    }
  },
}
</script>

<style lang="stylus" scoped></style>
